/**
 * useBillingCatalog — fetches the live, override-aware SKU catalog
 * for the customer-facing /billing page. Cards are grouped by
 * category so the page can render them in a deterministic order
 * regardless of what new SKUs are added to PACKAGES.
 *
 * Powered by GET /api/public/prices/catalog. Auto-invalidates after
 * the admin pricing page calls invalidatePublicPrices().
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

let _cache = null;
const _listeners = new Set();

export function invalidateBillingCatalog() {
  _cache = null;
  for (const fn of _listeners) try { fn(); } catch {}
}

export function useBillingCatalog() {
  const [data, setData] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    const sub = () => setData(null);
    _listeners.add(sub);
    return () => { _listeners.delete(sub); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (_cache) {
      setData(_cache);
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get("/public/prices/catalog")
      .then((r) => {
        if (cancelled) return;
        _cache = r.data;
        setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setData({ cards: [], count: 0 });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const cards = data?.cards || [];
  /** Look up a single card by package_id. */
  const find = (pkgId) => cards.find((c) => c.package_id === pkgId);
  /** Group cards by category. */
  const byCategory = cards.reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c);
    return acc;
  }, {});
  return { cards, byCategory, find, loading, raw: data };
}
